// Library
import React, { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router'

// Libs
import { AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material'
import { format, parseISO } from 'date-fns'

// CONST
import { RedTitle } from './global/RedTitle'

// Component
import Accordion from '../../../accordion/common/Accordion'
import ExhibitorResultsAccordion from './accordions/ExhibitorResultsAccordion'
import ViewsLoader from '../../../loader/ViewsLoader'

// FireBase
import FirestoreService from '../../../../services/firestoreService'
import RegistrationDyDayService from '../../../../services/firebase-service/registration-by-day/RegistrationDyDayService'

// Helper
import { extractDatePart, sortScheduling } from '../../../../helpers/time'
import { getCurrency } from '../../../../helpers/payment'
import { normalizeOrder } from '../../../../helpers/helpers'

// Store
import { getAllRegistrationFees } from '../../../../helpers/registrationFees'

import { EventFeesModel } from '../../../../models/event-fees/event-fees.model'
import { EventReviewPublishModel } from '../../../../models/event-review-publish/event-review-publish.model'
import { IEventReviewPublish } from '../../../../models/event-review-publish/event-review-publish.interface'
import { IRegistrationByDayInterface } from '../../../../models/registrations-by-day/registrationByDay.interface'
import { RegistrationFeesType } from '../../../../models/event-fees/event-fees.interface'

import { CONST } from '../../../../const/const'

interface IClassOption {
  value: string
  label: string
  parentId?: string
  location: string
  startDate: Date | string
  prizeMoney: number
  isAsc: boolean
  order: number
}

type IData = {
  data: IRegistrationByDayInterface[]
} & IClassOption

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

interface EventResultsProps {
  isTitleHide?: boolean
  hideEntries: boolean
}

const EventResults: FC<EventResultsProps> = ({ isTitleHide, hideEntries }) => {
  // Hooks and vars
  const eventId = useParams<{ eventId: string }>().eventId ?? null

  const [data, setData] = useState<IData[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [classOption, setClassOptions] = useState<IClassOption[]>([])
  const [publishedEvent, setPublishedEvent] = useState<IEventReviewPublish | null>(null)

  const currency = getCurrency(publishedEvent?.EventPaymentSettings?.currency)

  // Functions
  const handleGetFeesData = async () => {
    try {
      setLoading(true)

      const eventReviewPublishSnap = await FirestoreService.getItem(
        COLLECTIONS.EVENT_REVIEW_PUBLISH.NAME,
        eventId
      )
      const publishedEvent_ =
        EventReviewPublishModel.fromFirestoreDoc(eventReviewPublishSnap).toObject()
      setPublishedEvent(publishedEvent_)

      const eventFeesSnap = await FirestoreService.getItem(COLLECTIONS.EVENT_FEES.NAME, eventId)
      const eventFees = EventFeesModel.fromFirestoreDoc(eventFeesSnap).toObject()
      const flattenedFees = getAllRegistrationFees(eventFees?.registrationFees || [])

      const tempOptions: IClassOption[] = Object.values(
        sortScheduling(flattenedFees).reduce<Record<string, IClassOption>>((acc, item) => {
          acc[item.uuid] = {
            value: item.uuid,
            label: item.name || '',
            location: item.location,
            startDate: item.startDate,
            prizeMoney: (item as RegistrationFeesType)?.prizeMoney || 0,
            isAsc: (item as RegistrationFeesType)?.isAsc || false,
            order: (item as RegistrationFeesType)?.order || 0,
          }

          return acc
        }, {})
      )

      setClassOptions(tempOptions)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  // Fetching Riders and Horses from REGISTRATION_BY_DAY Collections (Depends on Class)
  const handleGetOrderData = async () => {
    setLoading(true)

    try {
      const filterClasses = await RegistrationDyDayService.getByEventIds([eventId])

      const uniqueData = filterClasses.reduce((acc: IRegistrationByDayInterface[], currentItem) => {
        // Check if the riderId of the current item is already in the accumulator
        const isRiderIdExist = acc.filter(
          (item) => item.riderId === currentItem.riderId && item.uuid === currentItem.uuid
        )

        // If riderId doesn't exist, add the current item to the accumulator
        if (isRiderIdExist.length <= 0) {
          acc.push({
            ...currentItem,
          })
        }
        return acc
      }, [])

      const combinedArray = classOption.map((item1) => {
        const correspondingItem = uniqueData.filter((item2) => item1.value === item2.uuid)
        return {
          ...item1,
          data: correspondingItem,
        }
      })

      setData(combinedArray)
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    handleGetFeesData().then()
  }, [])

  useEffect(() => {
    handleGetOrderData().then()
  }, [classOption])

  return (
    <>
      {!isTitleHide && <RedTitle text="Results" className="mb-4 sm:mb-7" />}
      {loading && (
        <div className="flex justify-center items-center h-[100px]">
          <ViewsLoader
            className="flex items-center w-full justify-center min-h-[30vh] mt-3"
            size="lg"
            color="#F70763"
          />
        </div>
      )}

      {Object.keys(data)?.[0]
        ? data
            ?.sort((a, b) => normalizeOrder(a.order) - normalizeOrder(b.order))
            .map((item, index) => {
              let isPublishScore
              let isPublishPrizeMoney
              const EventSchedule = publishedEvent?.EventSchedule

              if (publishedEvent?.EventSchedule?.scheduleBy === 'class') {
                const groupKey = item.value
                isPublishScore = EventSchedule?.classGroupPublish?.[groupKey]?.publishScore ?? false
                isPublishPrizeMoney =
                  EventSchedule?.classGroupPublish?.[groupKey]?.publishPrize ?? false
              } else {
                const extractedDate = extractDatePart(item.startDate)
                const parsedDate = parseISO(extractedDate)
                const dayKey = format(parsedDate, 'yyyy-MM-dd')
                const groupKey = `${dayKey}_${item.location}`
                isPublishScore = EventSchedule?.riderGroupPublish?.[groupKey]?.publishScore ?? false
                isPublishPrizeMoney =
                  EventSchedule?.riderGroupPublish?.[item.value]?.publishPrize ?? false
              }

              return (
                <Accordion
                  disabled={hideEntries}
                  key={index}
                  icon={<AddCircleOutline className="add_icon text-[red]" />}
                  initialState={false}
                  status={isPublishScore ? 'Available' : 'Pending'}
                  className="mb-2"
                  header={
                    <div className="text-[14px]">
                      {item.label}
                      {item.order ? (
                        <span className="ml-2 text-[14px] text-SeabiscuitMainThemeColor bg-SeabiscuitMainThemeColor/5 rounded-full py-0.5 px-3">
                          {item.order}
                        </span>
                      ) : null}
                    </div>
                  }
                  headerTextClassName="text-SeabiscuitDark200ThemeColor capitalize w-full pr-2"
                  activeIcon={<RemoveCircleOutline className="add_icon text-[red]" />}
                >
                  <ExhibitorResultsAccordion
                    {...item}
                    eventId={eventId}
                    publishScore={isPublishScore}
                    publishPrize={isPublishPrizeMoney}
                    currency={currency}
                  />
                </Accordion>
              )
            })
        : !loading && <p className="text-SeabiscuitDark200ThemeColor/50">Results not found</p>}
    </>
  )
}

export default EventResults
